import React, { useState, useEffect, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import './OnlinePlayers.css';
import PlayerList from './PlayerList';
import PlayerDetails from './PlayerDetails';

function OnlinePlayers({ onLookupVehicle }) {
  const { language } = useI18n();
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('autoRefresh');
    return saved !== null ? JSON.parse(saved) : true; // Default ON
  });

  const t = language === 'da'
    ? {
        failedFetch: 'Kunne ikke hente spillere',
        timeout: 'Anmodning fik timeout',
        loading: 'Indlæser online spillere...',
        error: 'Fejl',
        retry: 'Proev igen',
        connectionIssues: 'Forbindelsesproblemer (cachede data)',
        connected: 'Forbundet',
        autoRefresh: 'Auto-opdatering (15s)',
        refreshNow: 'Opdater nu',
        refresh: 'Opdater'
      }
    : {
        failedFetch: 'Failed to fetch players',
        timeout: 'Request timed out',
        loading: 'Loading online players...',
        error: 'Error',
        retry: 'Retry',
        connectionIssues: 'Connection issues (cached data)',
        connected: 'Connected',
        autoRefresh: 'Auto-refresh (15s)',
        refreshNow: 'Refresh Now',
        refresh: 'Refresh'
      };

  const failedFetchMessage = t.failedFetch;
  const timeoutMessage = t.timeout;

  const { apiFetch } = useAuth();

  const fetchPlayers = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await apiFetch('/api/players', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || failedFetchMessage);
      }
      
      const data = await response.json();
      setPlayers(data);
      setLoading(false);
      setError(null);
      setRetryCount(0); // Reset retry count on success
    } catch (err) {
      console.error('Error fetching players:', err);
      
      // Don't clear players on error - keep showing last known data
      setLoading(prev => false);
      
      // Only show error if we don't have any player data
      setError(prev => {
        if (prev === null) {
          return err.name === 'AbortError' ? timeoutMessage : err.message;
        }
        return prev;
      });
      setRetryCount(prev => prev + 1);
    }
  }, [apiFetch, failedFetchMessage, timeoutMessage]);

  const toggleAutoRefresh = () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    localStorage.setItem('autoRefresh', JSON.stringify(newValue));
  };

  useEffect(() => {
    fetchPlayers();
    if (autoRefresh) {
      const interval = setInterval(fetchPlayers, 15000); // Auto-refresh every 15 seconds
      return () => clearInterval(interval);
    }
  }, [fetchPlayers, autoRefresh]);

  if (loading) return <div className="loading">{t.loading}</div>;
  if (error) return (
    <div className="error">
      <p>{t.error}: {error}</p>
      <button onClick={fetchPlayers} className="retry-button">{t.retry}</button>
    </div>
  );

  return (
    <div className="online-players-container">
      <div className="online-players-header">
        <div className="connection-status">
          {retryCount > 0 && (
            <span className="warning-badge">{t.connectionIssues}</span>
          )}
          {retryCount === 0 && (
            <span className="success-badge">{t.connected}</span>
          )}
        </div>
        <div className="header-actions">
          <label className="auto-refresh-toggle">
            <input 
              type="checkbox" 
              checked={autoRefresh} 
              onChange={toggleAutoRefresh}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">{t.autoRefresh}</span>
          </label>
          <button onClick={fetchPlayers} className="refresh-button" title={t.refreshNow}>
            <span className="refresh-icon">*</span>
            <span>{t.refresh}</span>
          </button>
        </div>
      </div>
      <div className="players-grid">
        <PlayerList 
          players={players} 
          onSelectPlayer={setSelectedPlayer}
          selectedPlayerId={selectedPlayer?.id}
        />
        {selectedPlayer && (
          <PlayerDetails
            player={selectedPlayer}
            onLookupVehicle={onLookupVehicle}
          />
        )}
      </div>
    </div>
  );
}

export default OnlinePlayers;



